/**
 * Types + DTOs + Zod-schemas voor de communicatie-module (F7).
 *
 * Spec: docs/modules/communicatie.md.
 *
 * Een Bericht is een one-to-many broadcast (interne nieuws/aankondiging/vraag),
 * geen 1-op-1 chat. Reacties zijn optioneel (auteur vinkt aan).
 */

import { z } from 'zod'
import { queryBoolean } from './zod-query'
import { idParam } from './id-validators'

// ─── Enums (matching Prisma enums as string unions) ──────────────────────────

export const BERICHT_TYPE = ['AANKONDIGING', 'NIEUWS', 'VRAAG'] as const
export type BerichtType = (typeof BERICHT_TYPE)[number]

export const BERICHT_SCOPE = ['BEDRIJFSBREED', 'TEAM'] as const
export type BerichtScope = (typeof BERICHT_SCOPE)[number]

export const NOTIFICATIE_AUDIT_ACTIE = [
  'GEPLAATST',
  'GEWIJZIGD',
  'VERWIJDERD',
  'GEPIND',
  'ONTPIND',
  'REACTIE_GEPLAATST',
  'REACTIE_VERWIJDERD',
] as const
export type NotificatieAuditActie = (typeof NOTIFICATIE_AUDIT_ACTIE)[number]

// ─── Labels ──────────────────────────────────────────────────────────────────

export const BERICHT_TYPE_LABELS: Record<BerichtType, string> = {
  AANKONDIGING: 'Aankondiging',
  NIEUWS: 'Nieuws',
  VRAAG: 'Vraag',
}

export const BERICHT_SCOPE_LABELS: Record<BerichtScope, string> = {
  BEDRIJFSBREED: 'Bedrijfsbreed',
  TEAM: 'Team',
}

export const BERICHT_TYPE_BADGE_VARIANT: Record<
  BerichtType,
  'info' | 'warning' | 'success' | 'secondary'
> = {
  AANKONDIGING: 'warning',
  NIEUWS: 'info',
  VRAAG: 'success',
}

/**
 * Default per-type — `reactiesToegestaan` voor een nieuw bericht.
 *  - AANKONDIGING / NIEUWS: false (top-down, geen reactie-thread)
 *  - VRAAG: true (een vraag zonder antwoord-mogelijkheid heeft geen zin)
 */
export const DEFAULT_REACTIES_TOEGESTAAN: Record<BerichtType, boolean> = {
  AANKONDIGING: false,
  NIEUWS: false,
  VRAAG: true,
}

// ─── DTOs ────────────────────────────────────────────────────────────────────

export interface BerichtAuteurDTO {
  id: string
  name: string | null
  email: string
}

export interface BerichtListRowDTO {
  id: string
  type: BerichtType
  scope: BerichtScope
  departmentId: string | null
  departmentName: string | null
  titel: string
  inhoud: string
  reactiesToegestaan: boolean
  isPinned: boolean
  vervaltOp: string | null
  geplaatstDoor: BerichtAuteurDTO
  geplaatstOp: string
  /** Aantal reacties in de feed-kaart. */
  aantalReacties: number
  /** Aantal bijlagen (spec 41) — voor de 📎-indicator in de feed-kaart. */
  aantalBijlagen: number
  /** Heeft de huidige user dit bericht al gelezen? */
  gelezen: boolean
}

export interface BerichtReactieDTO {
  id: string
  berichtId: string
  geplaatstDoor: BerichtAuteurDTO
  inhoud: string
  createdAt: string
}

/**
 * Bijlage bij een bericht (spec 41). `downloadUrl` is het API-pad
 * (`/api/v1/...`) dat via de same-origin proxy-route van portal/backoffice
 * wordt opgehaald (Bearer server-side). `inBibliotheek` = de bijlage is óók als
 * bedrijfsbreed bibliotheek-document toegevoegd.
 */
export interface BerichtAttachmentDTO {
  id: string
  fileName: string
  fileSize: number
  mimeType: string
  sortOrder: number
  downloadUrl: string
  inBibliotheek: boolean
  createdAt: string
}

/** Bestandstypes die de portal/backoffice inline kan tonen (rest = download). */
export function isPreviewbaarMimeType(mimeType: string): boolean {
  return (
    mimeType === 'application/pdf' ||
    mimeType === 'image/jpeg' ||
    mimeType === 'image/png' ||
    mimeType === 'image/webp'
  )
}

export function isAfbeeldingMimeType(mimeType: string): boolean {
  return (
    mimeType === 'image/jpeg' ||
    mimeType === 'image/png' ||
    mimeType === 'image/webp'
  )
}

export interface BerichtDetailDTO extends BerichtListRowDTO {
  reacties: BerichtReactieDTO[]
  /** Bijlagen bij dit bericht (spec 41), gesorteerd op sortOrder. */
  bijlagen: BerichtAttachmentDTO[]
}

export interface BerichtOngelezenAantalDTO {
  aantal: number
}

// ─── Zod request-body schemas ────────────────────────────────────────────────

export const createBerichtSchema = z
  .object({
    type: z.enum(BERICHT_TYPE),
    scope: z.enum(BERICHT_SCOPE),
    departmentId: idParam().nullable().optional(),
    titel: z.string().trim().min(1).max(120),
    inhoud: z.string().trim().min(1).max(5000),
    reactiesToegestaan: z.boolean().optional(),
    isPinned: z.boolean().optional().default(false),
    vervaltOp: z.string().datetime().nullable().optional(),
  })
  .refine(
    (v) => (v.scope === 'TEAM' ? typeof v.departmentId === 'string' : true),
    {
      message: 'departmentId is verplicht bij scope=TEAM',
      path: ['departmentId'],
    },
  )
  .refine(
    (v) => (v.scope === 'BEDRIJFSBREED' ? !v.departmentId : true),
    {
      message: 'departmentId moet leeg zijn bij scope=BEDRIJFSBREED',
      path: ['departmentId'],
    },
  )

export type CreateBerichtInput = z.infer<typeof createBerichtSchema>

export const updateBerichtSchema = z.object({
  titel: z.string().trim().min(1).max(120).optional(),
  inhoud: z.string().trim().min(1).max(5000).optional(),
  isPinned: z.boolean().optional(),
  vervaltOp: z.string().datetime().nullable().optional(),
  reactiesToegestaan: z.boolean().optional(),
})

export type UpdateBerichtInput = z.infer<typeof updateBerichtSchema>

export const createReactieSchema = z.object({
  inhoud: z.string().trim().min(1).max(2000),
})

export type CreateReactieInput = z.infer<typeof createReactieSchema>

export const listBerichtenQuerySchema = z.object({
  type: z.enum(BERICHT_TYPE).optional(),
  scope: z.enum(BERICHT_SCOPE).optional(),
  departmentId: idParam().optional(),
  pinnedOnly: queryBoolean().optional(),
  ongelezenOnly: queryBoolean().optional(),
  vanaf: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  totEnMet: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  page: z.coerce.number().int().positive().default(1),
  pageSize: z.coerce.number().int().positive().max(100).default(20),
  /** Bij gebruik door dashboard-widget — beperkt resultaat tot top-N. */
  limit: z.coerce.number().int().positive().max(20).optional(),
})

export type ListBerichtenQuery = z.infer<typeof listBerichtenQuerySchema>
